import { useState } from "react";
import { toast } from "sonner";
import { Lock, Coins, Layers, Send, Radio } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { FOLLOWING, COLLECTIONS, THREADS, MESSAGES, FEED } from "../constants/demoData";
import { CreatorChip, PriceButton, usd } from "./DemoHome";

const PageHead = ({ title, sub, testId }) => (
  <div className="mb-6">
    <h1 data-testid={testId} className="font-display text-3xl font-extrabold tracking-tight text-ink">{title}</h1>
    <p className="mt-1 text-sm text-mist">{sub}</p>
  </div>
);

export const DemoFollowing = () => {
  const { t } = useLang();
  const f = t.demo.following;
  return (
    <div data-testid="demo-following" className="pb-20">
      <PageHead title={f.title} sub={f.sub} testId="demo-following-title" />
      <div className="grid gap-3 md:grid-cols-2">
        {FOLLOWING.map(({ creator, subscribed, priceUsd, renews }) => (
          <div key={creator.id} data-testid={`demo-following-${creator.id}`} className="card-soft flex items-center gap-4 p-4">
            <div className={`h-14 w-14 shrink-0 rounded-2xl bg-gradient-to-br ${creator.grad}`} />
            <div className="min-w-0 flex-1">
              <CreatorChip c={creator} />
              <p className="mt-1 text-xs text-mist">{creator.handle} · {creator.tag}</p>
              {subscribed ? (
                <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-800">● {f.active} · ${priceUsd}{f.perMonth} · {f.renews} {renews}</p>
              ) : (
                <p className="mt-1.5 inline-flex rounded-full bg-lilac px-2 py-0.5 text-[0.65rem] font-semibold text-ink-2">♥ {f.follow}</p>
              )}
            </div>
            {subscribed ? (
              <button onClick={() => toast(t.demo.toastPay)} className="pill btn-ghost px-4 py-2 text-xs font-semibold">{f.manage}</button>
            ) : (
              <button onClick={() => toast(t.demo.toastPay)} className="pill btn-primary px-4 py-2 text-xs font-semibold">{f.subscribe} · $9.99</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const DemoCollections = () => {
  const { t, lang } = useLang();
  const c = t.demo.collections;
  return (
    <div data-testid="demo-collections" className="pb-20">
      <PageHead title={c.title} sub={c.sub} testId="demo-collections-title" />
      <div className="grid gap-3 md:grid-cols-3">
        {COLLECTIONS.map((col) => (
          <article key={col.id} data-testid={`demo-collection-${col.id}`} className="card-soft overflow-hidden">
            <div className={`relative h-40 bg-gradient-to-br ${col.creator.grad}`}>
              <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[0.6rem] font-semibold text-ink"><Layers size={11} /> {col.pieces} {c.pieces}</span>
            </div>
            <div className="p-4">
              <CreatorChip c={col.creator} small />
              <p className="font-display mt-2 font-bold text-ink">{col.title[lang]}</p>
              <p className="mt-1 text-xs text-mist">{c.acquired} {col.date} · {c.paid} <Coins size={11} className="inline text-amber-500" /> {col.paid} ({usd(col.paid)})</p>
              <button className="pill btn-ghost mt-3 w-full py-2 text-xs font-semibold">{c.open}</button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export const DemoMessages = () => {
  const { t, lang } = useLang();
  const m = t.demo.messages;
  const [active, setActive] = useState(THREADS[0]);
  return (
    <div data-testid="demo-messages" className="pb-20">
      <PageHead title={m.title} sub={m.sub} testId="demo-messages-title" />
      <div className="card-soft grid overflow-hidden md:grid-cols-[280px_1fr]">
        <div className="border-b border-line md:border-b-0 md:border-r">
          {THREADS.map((th) => (
            <button key={th.id} data-testid={`demo-thread-${th.id}`} onClick={() => setActive(th)} className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${active.id === th.id ? "bg-violet-50" : "hover:bg-lilac"}`}>
              <div className={`h-10 w-10 shrink-0 rounded-full bg-gradient-to-br ${th.creator.grad}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between"><p className="truncate text-sm font-semibold text-ink">{th.creator.name}</p><span className="text-[0.65rem] text-mist">{th.time}</span></div>
                <p className="truncate text-xs text-ink-2">{th.last[lang]}</p>
              </div>
              {th.unread > 0 && <span className="h-5 min-w-5 rounded-full bg-pink-500 px-1.5 text-center text-[0.65rem] font-bold leading-5 text-white">{th.unread}</span>}
            </button>
          ))}
        </div>
        <div className="flex min-h-[420px] flex-col">
          <div className="flex items-center justify-between border-b border-line px-5 py-3"><CreatorChip c={active.creator} /><span className="text-xs text-mist">{active.creator.handle}</span></div>
          <div className="flex-1 space-y-3 p-5">
            {MESSAGES.map((msg) => (
              <div key={msg.id} className={`flex ${msg.from === "me" ? "justify-end" : ""}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${msg.from === "me" ? "bg-violet-600 text-white" : "bg-lilac text-ink"}`}>
                  {msg.ppv ? (
                    <div>
                      <div className={`mb-2 flex h-28 w-56 items-center justify-center rounded-xl bg-gradient-to-br ${active.creator.grad}`}><Lock size={18} className="text-white" /></div>
                      <p className="text-xs font-semibold">{m.ppv}</p>
                      <p className="text-xs text-ink-2">{msg.text[lang]}</p>
                      <div className="mt-2"><PriceButton tokens={msg.ppv} label={m.unlockFor} testId="demo-msg-unlock" /></div>
                    </div>
                  ) : msg.text[lang]}
                  <p className={`mt-1 text-[0.6rem] ${msg.from === "me" ? "text-violet-200" : "text-mist"}`}>{msg.time}</p>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); toast(t.demo.toastMsg); }} className="flex items-center gap-2 border-t border-line p-3">
            <input data-testid="demo-message-input" placeholder={m.placeholder} className="flex-1 rounded-full border border-line bg-canvas px-4 py-2 text-sm outline-none" />
            <button data-testid="demo-message-send" className="pill btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold"><Send size={13} /> {m.send}</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export const DemoLive = () => {
  const { t, lang } = useLang();
  const live = FEED[0];
  return (
    <div data-testid="demo-live" className="pb-20">
      <PageHead title={t.demo.nav.live} sub={t.demo.home.upcomingLive} testId="demo-live-title" />
      <div className="card-soft overflow-hidden">
        <div className={`relative flex h-72 items-center justify-center bg-gradient-to-br ${live.creator.grad}`}>
          <span className="eyebrow absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-ink"><Radio size={12} className="text-pink-500" /> {t.demo.home.upcomingLive} · 18 sep · 19:00</span>
          <Lock size={32} className="text-white/90" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div><CreatorChip c={live.creator} /><p className="font-display mt-2 text-lg font-bold text-ink">{live.title[lang]}</p><p className="text-xs text-mist">{t.demo.home.subscribers}</p></div>
          <PriceButton label={`${t.demo.home.subscribe} · $9.99/mo`} testId="demo-live-subscribe" />
        </div>
      </div>
    </div>
  );
};
